import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Mail } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import SEO, { SEO_CONFIGS } from '../components/SEO';
import NewsletterForm from '../components/NewsletterForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO {...SEO_CONFIGS.blogs} />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-primary text-white" data-testid="blogs-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl lg:text-6xl font-bold mb-6"
          >
            Insights & <span className="gold-text">Perspectives</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-300 max-w-3xl"
          >
            Industry trends, leadership insights, and executive recruitment perspectives
          </motion.p>
        </div>
      </section>

      {/* Blogs List */}
      <section className="section-spacing bg-white" data-testid="blogs-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12" data-testid="blogs-loading">
              <p className="text-muted-foreground">Loading articles...</p>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-12" data-testid="blogs-empty">
              <p className="text-muted-foreground">No articles available.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blogs/${blog.slug}`}
                  data-testid={`blog-card-${blog.id}`}
                  className="group block bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  {blog.image_url && (
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={blog.image_url}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-secondary text-primary px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                          {blog.category}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {format(new Date(blog.created_at), 'MMM dd, yyyy')}
                      </span>
                      <span className="flex items-center">
                        <User size={14} className="mr-1" />
                        {blog.author}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-secondary transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
                      {blog.excerpt}
                    </p>
                    <span className="inline-flex items-center text-secondary font-semibold text-sm">
                      Read More <ArrowRight className="ml-2" size={16} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-muted" data-testid="blogs-newsletter">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-l-4 border-secondary shadow-sm p-8 md:p-12">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="text-secondary" size={24} />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Newsletter</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">Subscribe to Leadership Hiring Insights</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl">
              Get our latest articles on executive search, leadership trends, and talent strategy delivered to your inbox.
            </p>
            <NewsletterForm source="blog" variant="light" testId="blogs-newsletter-form" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blogs;